import type { ComponentType, ReactNode } from 'react';
import type { LabelColor, LabelVariant } from '../../Label';
import type { TableCellProps } from '@mui/material';

export type Row<T> = {
  id: string;
} & T;

type Columns = string;

type Filters = string;

type Alignment = 'left' | 'center' | 'right';

type DefaultSort = 'ASC' | 'DESC';

interface Column<Key extends string, T extends Row<unknown>> {
  key: Key;
  label: string;
  /** Secondary header line, for columns that show two values per cell. */
  subLabel?: string;
  component: ComponentType<{
    row: T;
  }>;
  align?: Alignment;
  /**
   * If the callback is provided, it will enable sorting for this column.
   */
  onSort?: (direction: DefaultSort) => void;
  minWidth?: number;
  maxWidth?: number;
  sx?: TableCellProps['sx'];
}

/**
 * An item in the tab bar
 */
export interface TabItem<T> {
  /// The internal value of the tab
  value: T;
  /// The translation key for the label
  label: string;
  /// The color that the count will be displayed in. Or the color
  /// of the label if no count is provided
  color: LabelColor;
  variant?: LabelVariant;
  /// The count to be displayed in the tab. If a promise is returned,
  /// the tab count will be displayed as loading until the promise resolves
  count?: number | Promise<number>;
  /// Draws a vertical line after this tab, to group it apart from the rest
  separatorAfter?: boolean;
}

/**
 * @template filterModel The object containing filter data
 */
export interface Tabs<T> {
  /**
   * The items to be displayed in the tabs
   */
  items: Array<TabItem<T>>;
  updateFilterModel: (selectedItem: T) => void;
  selectedItem?: T;
}

export interface RowAction<T extends Row<unknown>> {
  /**
   * A component that renders a menu item
   */
  component?: ComponentType<{
    row: T;
  }>;

  // Properties if no custom component is necessary

  icon?: ReactNode;
  /// The translation key for the label
  label?: string;
  key?: string;
  errorVariant?: boolean;
  onClick: (row: T) => void | Promise<void>;
}

interface Toolbar {
  primarySelection?: {
    component: (props: { onChange: (value: any) => void }) => React.ReactNode;
    onChange: (value: any) => void;
  };
  searchBar?: {
    query: string;
    onQueryChange: (query: string) => void;
    label: string;
  };
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<
    | number
    | {
        value: number;
        label: string;
      }
  >;

  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export type FilterDefinition<FilterKey> =
  | FilterDefinitionBoolean<FilterKey>
  | FilterDefinitionCustom<FilterKey>
  | FilterDefinitionNumberRange<FilterKey>
  | FilterDefinitionDateRange<FilterKey>
  | FilterDefinitionText<FilterKey>
  | FilterDefinitionSelect<FilterKey>;

interface FilterDefinitionBase<FilterKey> {
  key: FilterKey;
  label: string;
  // Built-in types derive these from their value. Custom filters must provide
  // them to show as set and to be clearable.
  isSet?: () => boolean;
  toStringRepresentation?: () => string;
  unset?: () => void;
  // For filters that always hold a value, like one defaulting to the user's
  // own scope: no clear icon, and clear-all leaves them alone.
  clearable?: boolean;
  // Pills variant only: shown in the table header next to the filter button,
  // even while the filter row is collapsed. For the one or two filters most
  // users reach for, like a free text search or the main date filter.
  highlighted?: boolean;
}

interface FilterDefinitionCustom<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'custom';
  // An element rather than a component type: a fresh inline component per
  // config rebuild would remount the input on every keystroke.
  component: ReactNode;
}

interface FilterDefinitionBoolean<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'boolean';
  value: boolean;
  onChange: (value: boolean) => void;
  color?: 'primary' | 'error';
}

export interface FilterDefinitionNumberRange<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'numberRange';
  min?: number;
  max?: number;
  range:
    | {
        from?: number;
        to?: number;
      }
    | undefined;
  onChange: (
    range:
      | {
          from?: number;
          to?: number;
        }
      | undefined
  ) => void;
}

export interface FilterDefinitionDateRange<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'dateRange';
  min?: Date;
  max?: Date;
  range?: {
    from?: Date;
    to?: Date;
  };
  onChange: (range?: { from?: Date; to?: Date }) => void;
}

export interface FilterDefinitionText<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'text';
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  inline?: boolean;
}

export interface SelectOption {
  id: string;
  label: string;
}

export interface FilterDefinitionSelect<FilterKey>
  extends FilterDefinitionBase<FilterKey> {
  type: 'select';
  value: string | undefined;
  // A value missing from a non-empty options list is cleared by the table, so
  // a scope switch cannot leave a stale id filtering an empty table.
  options: SelectOption[];
  onChange: (id: string | undefined) => void;
}

interface RowOptions<T extends Row<unknown>> {
  /**
   * Items that should be displayed. If the items differ per row,
   * the callback can be used to generate the items.
   *
   * If the array is empty, no menu will be displayed
   */
  items: Array<RowAction<T>> | ((row: T) => Array<RowAction<T>>);
}

export interface SelectionConfig {
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: (allIds: string[]) => void;
}

export interface ConfigType<
  T extends Row<unknown>,
  ColumnKeys extends Columns,
  FilterKey extends Filters
> {
  onClickRow?: (row: T) => void | Promise<void>;
  selection?: SelectionConfig;
  hoverRow?: boolean;
  showAll?: () => void;
  columns: Array<Column<ColumnKeys, T>>;

  /**
   * If defined, each row will have the ic_more menu
   */
  options?: RowOptions<T>;

  /**
   *  The sort column and sort direction for the table. If not provided, no sort indicator is shown.
   *
   *  This should be in sync with the sort in the filter model.
   */
  sortColumn?: {
    column: ColumnKeys;
    direction: DefaultSort;
  };

  /**
   * The component to render when there is no data available. Or a string that will be used as a translation key and shown
   * below the default image.
   */
  noDataAvailable?: ComponentType | string;
  tabs?: Tabs<any>;

  /**
   * Header text for a table with pill filters but no tabs. Without it a single
   * "All" tab fills the header row.
   */
  title?: string;

  /**
   * The height for the rows when they are empty. Defaults to 60.
   */
  emptyRowHeight?: number;

  /**
   * Keeps the table only as tall as its rows instead of padding a short page
   * up to the page size.
   */
  hideEmptyRows?: boolean;

  /**
   * The toolbar that usually contains a search input
   */
  toolbar?: Toolbar;

  /**
   * The pagination displayed at the bottom of the table
   */
  pagination?: Pagination;

  // Controls available filters - they will also store state to display them as chips
  filters?: {
    items: Array<FilterDefinition<FilterKey>>;
    // Defaults to the number of set items.
    activeFilters?: number;
    noActiveFiltersLabel?: string;
    showActiveFiltersBar?: boolean;
    // 'pills' renders inline filter chips with an inline editor instead of the
    // drawer button and the active filters bar.
    variant?: 'drawer' | 'pills';
  };
}

export const makeConfig = <
  T extends Row<unknown>,
  ColumnKeys extends Columns,
  FilterKey extends Filters
>(
  config: ConfigType<T, ColumnKeys, FilterKey>
): ConfigType<T, ColumnKeys, FilterKey> => config;
